import { useState, useEffect, useRef, useCallback } from 'react';
import { createKeyboardDismissDetector } from '../utils/keyboardDismiss';
import { revealAfterKeyboard, onViewportSettled, traceFocusOpen, debugLine } from '../utils/revealBelowSticky';

/**
 * useMobileFocus — the page-level mobile focus card for text inputs (< 1024px).
 *
 * A tap on a focus trigger moves editing into `Clothing/Common/MobileFocus/
 * MobileFocusModal`, a card that floats above the software keyboard, so the field
 * is never buried under a sticky element or the keyboard itself.
 *
 * Markup contract (see MobileFocusModal.css):
 *   - trigger wrapper: `id="mf-trigger-<fieldKey>"` + `className="mf__trigger"`
 *   - modal root:      `ref={activeFocusContainerRef}`, `mf__modal` (+ `--active`, `--opening`)
 *   - modal input:     `id="mf-input"`
 *
 * @param {object} reveal Where the field must land when focus mode closes:
 *   `{ section, sticky, gap, align }` — the trigger's `section` ancestor is brought
 *   `gap` px below the `sticky` element. Product pages pass `PRODUCT_FOCUS_REVEAL`
 *   (`Clothing/Common/Product/focusReveal.js`); other pages pass their own selectors.
 * @param {string|Function} [keepClear] The product area the card's scrim must NOT
 *   cover: a selector (the scrim starts at that element's bottom; defaults to
 *   `reveal.sticky` — the sticky gallery / preview) or `() => number|null`, the
 *   layout-viewport y where the scrim starts (e.g. a drawer's top edge). The scrim hides the page UI behind the card (the very field being
 *   edited, duplicated under it) from this element's bottom down to the keyboard,
 *   so the customer still sees the product they are personalising.
 */
export default function useMobileFocus({ reveal, keepClear = reveal.sticky }) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 1023px)').matches : false
  );
  const [focusField, setFocusField] = useState(null);

  const activeFocusContainerRef = useRef(null);
  const scrollYBeforeFocus = useRef(0);
  // Pending focus-exit reveal (cancel fn) and the open-time focus timers.
  const revealCancelRef = useRef(null);
  const openTimersRef = useRef([]);
  // Settle wait of the opening phase (see openFocusMode).
  const openSettleCancelRef = useRef(null);
  useEffect(() => () => {
    revealCancelRef.current?.();
    openSettleCancelRef.current?.();
    openTimersRef.current.forEach(clearTimeout);
  }, []);

  /* Resize */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* Scrim top: the keep-clear element's bottom in the modal's own coordinates
   * (the modal is pinned to the visual viewport, rects are layout-viewport). */
  const updateScrimTop = useCallback(() => {
    const modal = activeFocusContainerRef.current;
    if (!modal) return;
    let edge = null;
    if (typeof keepClear === 'function') edge = keepClear();
    else if (keepClear) edge = document.querySelector(keepClear)?.getBoundingClientRect().bottom ?? null;
    const offset = window.visualViewport?.offsetTop ?? 0;
    const top = edge == null ? 0 : Math.max(0, edge - offset);
    const value = Math.round(top) + 'px';
    if (modal.style.getPropertyValue('--mf-scrim-top') !== value) {
      modal.style.setProperty('--mf-scrim-top', value);
    }
  }, [keepClear]);

  /* While the card is open, follow the keep-clear element every frame. The
   * product area can move or resize with NO event to hook: Chrome iOS shrinks
   * the layout viewport for the keyboard and the Overall stage then animates
   * its product box down over a drawer transition — a one-off measurement on
   * the viewport resize caught it mid-animation and left the scrim's top
   * ~100px low. One rect read per frame, only while the card is up. */
  useEffect(() => {
    if (!focusField) return undefined;
    let raf = 0;
    const tick = () => {
      updateScrimTop();
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [focusField, updateScrimTop]);

  /* Focus mode */
  const openFocusMode = useCallback((fieldKey) => {
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    // A new focus cancels a reveal still pending from the previous close.
    revealCancelRef.current?.();
    revealCancelRef.current = null;
    openTimersRef.current.forEach(clearTimeout);
    openTimersRef.current = [];
    scrollYBeforeFocus.current = window.scrollY;
    const modal = activeFocusContainerRef.current;
    const modalInput = document.getElementById('mf-input');
    if (modal && modalInput) {
      traceFocusOpen(fieldKey);
      // Opening phase. The input gets focus while the card is parked at the
      // TOP of the visual viewport, invisible: it is then already clear of
      // where the keyboard will appear, so iOS has no reason to pan the
      // viewport. (Focused at the bottom, iOS panned, and the fixed 3D column
      // and the card chased that pan in steps while the page was also reset
      // to the top — three motions during one keyboard animation = "jumpy".
      // The dropdown sheet never jumped for exactly this reason: its input is
      // at the top.) Once the viewport has settled the card takes its place
      // above the keyboard and fades in. No scroll reset: nothing to undo.
      updateScrimTop();
      modal.classList.add('mf__modal--active', 'mf__modal--opening');
      modalInput.focus();
      openSettleCancelRef.current?.();
      openSettleCancelRef.current = onViewportSettled(() => {
        openSettleCancelRef.current = null;
        modal.classList.remove('mf__modal--opening');
        debugLine('open settled → card shown');
      }, { initialDelay: 250, quietDelay: 80, maxWait: 800 });
      openTimersRef.current = [setTimeout(() => modalInput.focus(), 100)];
    }
    setFocusField(fieldKey);
  }, [updateScrimTop]);

  const closeFocusMode = useCallback(() => {
    if (!focusField) return;
    const currentField = focusField;
    setFocusField(null);
    const modal = activeFocusContainerRef.current;
    if (modal) modal.classList.remove('mf__modal--active', 'mf__modal--opening');
    openSettleCancelRef.current?.();
    openSettleCancelRef.current = null;
    const modalInput = document.getElementById('mf-input');
    if (modalInput) modalInput.blur();
    if (document.activeElement && document.activeElement.tagName === 'INPUT') {
      document.activeElement.blur();
    }
    openTimersRef.current.forEach(clearTimeout);
    openTimersRef.current = [];
    revealCancelRef.current?.();
    revealCancelRef.current = null;
    const triggerId = 'mf-trigger-' + currentField;
    if (!document.getElementById(triggerId)) {
      window.scrollTo({ top: scrollYBeforeFocus.current, behavior: 'auto' });
      return;
    }
    // Land the field's section under the host's sticky element: wait for the
    // keyboard / visual viewport to settle, then converge over a few frames (the
    // sticky bottom moves as the header scrolls out, and on 3D product pages it
    // flips from fixed back to sticky once `pp--typing` drops).
    // See utils/revealBelowSticky.js.
    revealCancelRef.current = revealAfterKeyboard({
      target: () => {
        const trigger = document.getElementById(triggerId);
        return trigger ? (trigger.closest(reveal.section) || trigger) : null;
      },
      sticky: () => document.querySelector(reveal.sticky),
      gap: reveal.gap,
      align: reveal.align,
      onDone: () => { revealCancelRef.current = null; },
    });
  }, [focusField, reveal]);

  /* No page scroll while the card is open. A drag on the card (or an iOS
   * visual-viewport pan) scrolled the page behind it: the product slid away
   * and the scrim followed. A drag inside the field still moves the caret /
   * selection. (A drag starting on the backdrop closes the card on pointerdown
   * first, then scrolls as usual.) */
  useEffect(() => {
    const modal = activeFocusContainerRef.current;
    if (!focusField || !modal) return undefined;
    const blockScroll = (e) => {
      if (e.target.closest?.('input, textarea')) return;
      if (e.cancelable) e.preventDefault();
    };
    modal.addEventListener('touchmove', blockScroll, { passive: false });
    return () => modal.removeEventListener('touchmove', blockScroll);
  }, [focusField]);

  /* visualViewport tracking */
  useEffect(() => {
    // Keyboard collapsed without a blur (iOS swipe-down, Android back):
    // leave focus mode. The detector copes with Chrome's stepwise keyboard
    // animation, where no single resize is a 100px jump.
    const keyboardDismissed = createKeyboardDismissDetector(window.visualViewport?.height ?? window.innerHeight);
    const updateVH = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const offset = window.visualViewport?.offsetTop ?? 0;
      if (activeFocusContainerRef.current) {
        activeFocusContainerRef.current.style.setProperty('--mf-vp-height', vh + 'px');
        activeFocusContainerRef.current.style.setProperty('--mf-vp-offset', offset + 'px');
      }
      updateScrimTop();
      if (keyboardDismissed(vh)) {
        if (activeFocusContainerRef.current && activeFocusContainerRef.current.classList.contains('mf__modal--active')) {
          setTimeout(() => closeFocusMode(), 50);
        }
      }
    };
    updateVH();
    window.visualViewport?.addEventListener('resize', updateVH);
    window.visualViewport?.addEventListener('scroll', updateVH);
    return () => {
      window.visualViewport?.removeEventListener('resize', updateVH);
      window.visualViewport?.removeEventListener('scroll', updateVH);
    };
  }, [closeFocusMode, updateScrimTop]);

  return {
    isMobile,
    focusField,
    activeFocusContainerRef,
    openFocusMode,
    closeFocusMode,
  };
}
